import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const previousLanguageRef = useRef('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'en';
    setLanguage(savedLanguage);
    previousLanguageRef.current = savedLanguage;
    applyLanguageSettings(savedLanguage);
  }, []);

  const applyLanguageSettings = (langCode) => {
    document.documentElement.dir = langCode === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = langCode;
    
    // Update CSS custom properties for RTL/LTR
    if (langCode === 'ar') {
      document.documentElement?.style?.setProperty('--text-align', 'right');
      document.documentElement?.style?.setProperty('--flex-direction', 'row-reverse');
    } else {
      document.documentElement?.style?.setProperty('--text-align', 'left');
      document.documentElement?.style?.setProperty('--flex-direction', 'row');
    }
  };

  const changeLanguage = (langCode) => {
    // Save current scroll position before any changes
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    const previousLanguage = previousLanguageRef.current;
    setLanguage(langCode);
    previousLanguageRef.current = langCode;
    localStorage.setItem('memorial-language', langCode);
    
    applyLanguageSettings(langCode);
    
    // Trigger a custom event to notify other components (for backward compatibility)
    window.dispatchEvent(new CustomEvent('languageChanged', { 
      detail: { language: langCode } 
    }));
    
    // Restore scroll position after DOM updates
    // Use multiple requestAnimationFrame to ensure DOM has fully updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({
          left: scrollX,
          top: scrollY,
          behavior: 'auto' // Instant scroll, no animation
        });
      });
    });
  };

  const direction = language === 'ar' ? 'rtl' : 'ltr';

  // Translation function (placeholder - can be expanded with i18n library)
  const t = (key) => {
    // For now, return the key - can be replaced with actual translation logic
    return key;
  };

  const value = {
    language,
    direction,
    changeLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

